/* jscpd:ignore-start */
import { makePaginatedSearch } from "api/makeApiRequestType/makePaginatedSearch";
import { convertEventDatesToJavascriptDates } from "api/utils/convertEventDatesToJavascriptDates";
import { formatDataForGetRequest } from "api/utils/formatDataForGetRequest";
import {
	ValidationCallback,
	validate,
	validationErrorsToString
} from "validation/validate";
import { Medium } from "hooks/useAvailableMediums";
import {
	flagValidationErrorsInSingleSessionPrice,
	SingleSessionPrice
} from "types-and-validators/SingleSessionPrice";
import {
	flagValidationErrorsInMeetingTimeSlotRawFromApi,
	MeetingTimeSlotRawFromApi
} from "types-and-validators/MeetingTimeSlotRawFromApi";
import { SessionType } from "types-and-validators/SessionType";
import { PaymentStatus } from "types-and-validators/PaymentStatus";
/* jscpd:ignore-end */

type RawApiOutputBaseData = {
	id: string;
} & MeetingTimeSlotRawFromApi;

// TODO:WV:20230421:Make sure these are all validated correctly
export interface AttendeeData {
	hostUid: string;
	guestUid: string;
	displayNameHost: string;
	displayNameGuest: string;
	paymentStatus: PaymentStatus;
	singleSessionPrice: SingleSessionPrice;
	sessionType: SessionType;
	isSignedOffByGuest: boolean;
	isSignedOffBySystem: boolean;
	medium: Medium;
	joinURL: string | undefined;
	hostSkypeId: string | undefined;
	guestSkypeId: string | undefined;
}

export interface HostData {
	startURL: string | undefined;
}

interface RawApiOutputRow extends RawApiOutputBaseData {
	attendeeData?: AttendeeData;
	hostData?: HostData;
}

const flagValidationErrorsInAttendeeData: ValidationCallback<AttendeeData> = (
	input,
	{ flag, checkField },
	{ uid }
) => {
	checkField("hostUid", { type: "string" });
	checkField("guestUid", { type: "string" });
	checkField("displayNameHost", { type: "string" });
	checkField("displayNameGuest", { type: "string" });
	checkField("medium", { type: "string" });
	checkField("paymentStatus", {
		type: "enum",
		allowedValues: ["free", "pending", "ok"],
		isRequired: true
	});

	if (input.singleSessionPrice) {
		validate(input.singleSessionPrice, {
			doValidate: flagValidationErrorsInSingleSessionPrice,
			withErrors: errors => {
				flag("singleSessionPrice", validationErrorsToString(errors));
			},
			auth: { uid }
		});
	}

	checkField("isSignedOffByGuest", {
		type: "boolean"
	});
	checkField("isSignedOffBySystem", {
		type: "boolean"
	});
	checkField("joinURL", {
		type: "string",
		isRequired: false
	});

	checkField("hostSkypeId", {
		type: "string",
		isRequired: false
	});

	checkField("guestSkypeId", {
		type: "string",
		isRequired: false
	});
};

const flagValidationErrorsInHostData: ValidationCallback<HostData> = (
	input,
	{ flag, checkField },
	{ uid }
) => {
	checkField("startURL", {
		type: "string",
		isRequired: false
	});
};

const flagValidationErrorsInRow: ValidationCallback<RawApiOutputRow> = (
	input,
	utils,
	auth
) => {
	const { flag, checkField } = utils;

	checkField("id", { type: "string" });

	flagValidationErrorsInMeetingTimeSlotRawFromApi(input, utils, auth);

	const { uid } = auth;
	const userIsHost = !!uid && (!!input.hostUid && input.hostUid === uid);
	const userIsGuest = !!uid && (!!input.guestUid && input.guestUid === uid);
	const isOwnMeeting = !!userIsHost || !!userIsGuest;

	if (isOwnMeeting) {
		const { attendeeData } = input;
		if (!attendeeData) {
			flag("attendeeData", "missing");
		} else {
			validate(input.attendeeData, {
				auth,
				doValidate: flagValidationErrorsInAttendeeData,
				withErrors: errors => {
					flag("attendeeData", JSON.stringify(errors));
				}
			});
		}
	}

	if (userIsHost) {
		const { hostData } = input;
		if (!hostData) {
			flag("hostData", "missing");
		} else {
			validate(input.hostData, {
				auth,
				doValidate: flagValidationErrorsInHostData,
				withErrors: errors => {
					flag("hostData", JSON.stringify(errors));
				}
			});
		}
	}
};

export interface OutputRow
	extends Omit<RawApiOutputRow, "dateStartsUTC" | "dateEndsJustBeforeUTC"> {
	dateStartsUTC: Date;
	dateEndsJustBeforeUTC: Date;
}

export const getMeetings = makePaginatedSearch<
	{
		id?: string;
		hostUid?: string;
		guestUid?: string;
		paymentStatus?: string;
		isSignedOff?: string;
		earliestStartDateUTC?: Date;
		mustStartBeforeUTC?: Date;
		mustEndAfterUTC?: Date;
		mustEndBeforeUTC?: Date;
		source?: string;
	},
	RawApiOutputRow,
	OutputRow
>({
	endPoint: "meetings",
	flagValidationErrorsInRow,
	postProcessor: convertEventDatesToJavascriptDates,
	preProcessor: formatDataForGetRequest
});
